import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import {
  AngularToReactDataGridBridge,
  IDataGridPropsScope,
} from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { AlertErrorCode, CommonErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { ISliceable } from '../models/ISliceable';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IError } from '../ServerApi/types/IError';
import { ModalService, ModalSize } from './ModalService';
import { SliceableFactory } from './SliceableFactory';
import { ToasterService, ToastType } from './ToasterService';

export interface IErrorDetail {
  itemName: string;
  itemUrl?: string;
  errorMessage: string;
}

export interface IMultipleResultTranslator<T> {
  describeSuccessSummary(successCount: number): string;
  describeErrorSummary(errorCount: number): string;
  describeErrorDetail(errorItem: IErrorItem<T>): IErrorDetail;
}

export interface IMultipleResultNotificationOptions {
  successSummary?: string;
  errorSummary?: string;
  errorDetails?: IErrorDetail[];
}

export module MultipleResultNotificationOptions {

  export function create<T>(
    result: MultipleItemResult<T>,
    translator: IMultipleResultTranslator<T>): IMultipleResultNotificationOptions {

    const options: IMultipleResultNotificationOptions = { };

    if (result.anySucceeded()) {
      options.successSummary = translator.describeSuccessSummary(result.successCount);
    }
    if (result.anyFailed()) {
      options.errorSummary = translator.describeErrorSummary(result.errorCount);
      options.errorDetails = _.compact(_.map(
          result.errorItems(),
          (errorItem: IErrorItem<T>) => translator.describeErrorDetail(errorItem)
      ));
    }

    return options;
  }

}

export interface INotificationTranslationIds {
  successId: string;
  errorId: string;
}


interface ContentActionNotificationToastContentProps {
  options: IMultipleResultNotificationOptions;
  detailsLinkText: string;
  openModal: () => void;
}

export class ContentActionNotification {
  private toaster = ToasterService.instance;
  public static $inject = [
    '$translate',
    '$rootScope',
    'ModalService',
    'SliceableFactory',
    'AngularToReactDataGridBridge'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private scope: ng.IScope,
    private modal: ModalService,
    private SliceableFactory: SliceableFactory,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge) {}

  public notifyResult(
    items: Set<INamedItemInfo>,
    errors: IError[],
    translations: INotificationTranslationIds,
    translationParams?: Object) {

    const errorCount = errors ? errors.length : 0;
    const successCount = items.size() - errorCount;

    const notificationLines: string[] = [];

    if (successCount) {
      notificationLines.push(this.$translate.instant(
          translations.successId,
          angular.extend({ ITEM_COUNT: successCount }, translationParams),
          L10n.Interpolations.Count)
      );
    }

    if (errorCount) {
      if (!_.isUndefined(errors) && _.isUndefined(errors[0].id)) {
        notificationLines.push(this.notifyFailureMessage(errors[0]));
      } else {
        notificationLines.push(this.$translate.instant(
          translations.errorId,
          angular.extend({ ERROR_COUNT: errorCount }, translationParams),
          L10n.Interpolations.Count)
        );
      }
    }

    this.toaster.pop({ type: errorCount ? ToastType.Error : ToastType.Info, text: notificationLines.join('\n') });
  }

  public notify(options: IMultipleResultNotificationOptions) {

    if (options.successSummary) {
      this.toaster.pop({ type: ToastType.Info, text: options.successSummary });
    }

    if (options.errorSummary) {
      this.showToast(options);
    }
  }

  public notifyGenericError(error: IError) {
    return this.notifyFailure(this.notifyFailureMessage(error));
  }

  private notifyFailureMessage(error: IError) {
    switch(error.code) {
      case CommonErrorCode.TooManyRequests:
        return this.handleTooManyRequests(error);
      case AlertErrorCode.AlertConnectionlessFailure:
        return this.$translate.instant('credentialMissedOrWrong');
    }
    return this.$translate.instant('errorUnknown');
  }

  handleTooManyRequests(error: IError) {
    let retryAfter: number = error.errorMetaData.retryAfter;
    let text: string = '';
    // constructs throttling error message "Too many requests. Please retry after {text}"
    // text can be in hours, hours and minutes or seconds
    if (retryAfter >= 3600) {
      text = text + this.$translate.instant('hours', { HOURS: Math.floor(retryAfter/3600) }, L10n.Interpolations.Count);
      retryAfter %= 3600;
    }
    if (retryAfter >= 60) {
      const minutes: string = this.$translate.instant('minutes', { MINUTES: Math.floor(retryAfter/60) }, L10n.Interpolations.Count);
      text = text === ''?minutes:text + ' ' + minutes;
      retryAfter = 0;
    }
    // if hours or minutes is set dont include seconds
    if (retryAfter > 0 && text === '') {
      text = this.$translate.instant('seconds', { SECONDS: Math.floor(retryAfter) }, L10n.Interpolations.Count);
    }
    return this.$translate.instant('tooManyRequests', { RETRY_AFTER_TEXT: text });
  }

  private showToast(options: IMultipleResultNotificationOptions) {
    const toastContentProps: ContentActionNotificationToastContentProps = {
      options: options,
      detailsLinkText: this.$translate.instant('details'),
      openModal: () => this.openErrorDetailsModalFromToast(this.scope, options)
    };

    this.toaster.pop({
      type: ToastType.Error,
      text: toastContentProps.options.errorSummary,
      actionMessage: toastContentProps.detailsLinkText,
      action: () => toastContentProps.openModal()
    });
  }

  private openErrorDetailsModalFromToast(
      parentScope: ng.IScope,
      options: IMultipleResultNotificationOptions) {

    const dataGridScope = parentScope.$new() as IDataGridPropsScope<IErrorDetail>;
    const sliceable = this.SliceableFactory.fromArray(options.errorDetails);
    this.configureReactErrorDetailsGrid(dataGridScope, sliceable);

    this.modal.open({
      scope: dataGridScope,
      templateUrl: 'errorDetailsDialog.html',
      size: ModalSize.fitToContent,
    }).result.finally(() => dataGridScope.$destroy());
  }

  private configureReactErrorDetailsGrid(dataGridScope: IDataGridPropsScope<IErrorDetail>,
                                         sliceable: ISliceable<IErrorDetail>) {
    const cols: DataGrid.Column[] = [
      {
        name: 'itemName-col',
        dataKey: 'itemName',
        headerText: this.$translate.instant('column_error_item_name'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCell('itemName'),
        sortable: false
      },
      {
        name: 'errorMessage-col',
        dataKey: 'errorMessage',
        headerText: this.$translate.instant('column_error_message'),
        sortable: false,
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCell('errorMessage')
      }
    ];

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: dataGridScope,
      columns: cols,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor({}),
      canShowFilterPanel: false
    });
    this.AngularToReactDataGridBridge.updateReactDataGridRowStore({
      scope: dataGridScope, sliceable: sliceable });
  }

  public notifySuccess(successMessage: string){
    this.toaster.pop({ type: ToastType.Info,  text: successMessage });
  }

  public notifyFailure(failureMessage: string){
    this.toaster.pop({ type: ToastType.Error, text: failureMessage });
  }

}

Module.instance().service('ContentActionNotification', ContentActionNotification);
